import { type BTLoginTypes } from "types/enum";

import { convertFileSizeToBytes, FileSizeUnit } from "utilities/file/file";
import { appendPortalTypeToUrl } from "utilities/portal/portal";

const MAX_FILE_SIZE_MB = 250;
const ALLOWED_EXTENSION = ".pdf";
const MIN_FILE_SIZE_BYTES = 500;

export interface IPlanFileValidationResult {
    isValid: boolean;
    error?: string;
}

/**
 * Validates a file for plans upload. Checks extension (when validateType is true),
 * minimum file size, and maximum file size.
 */
export function validatePlanFile(
    fileName: string,
    fileSize: number,
    validateType: boolean,
): IPlanFileValidationResult {
    const lastDotIndex = fileName.lastIndexOf(".");
    const extension = lastDotIndex === -1 ? "" : fileName.substring(lastDotIndex).toLowerCase();

    if (validateType && extension !== ALLOWED_EXTENSION) {
        const displayExtension = extension === "" ? "(no extension)" : extension;
        return {
            isValid: false,
            error: `Only PDF files are allowed. Selected: ${displayExtension}`,
        };
    }

    if (fileSize < MIN_FILE_SIZE_BYTES) {
        return {
            isValid: false,
            error: "File appears to be empty or invalid",
        };
    }

    const maxSizeBytes = convertFileSizeToBytes(MAX_FILE_SIZE_MB, FileSizeUnit.Megabyte);
    if (fileSize > maxSizeBytes) {
        const fileSizeMB = (fileSize / (1024 * 1024)).toFixed(2);
        return {
            isValid: false,
            error: `File size (${fileSizeMB} MB) exceeds ${MAX_FILE_SIZE_MB} MB limit`,
        };
    }

    return { isValid: true };
}

/**
 * Builds a direct browser URL from an orval query key tuple, appending portalType as a query string parameter.
 * Use this for plan file/thumbnail URLs loaded via img src, iframe, or window.open.
 * Couples to orval's query key format: [path, params?]. If orval changes this structure, this function must be updated.
 * @param queryKey The orval query key tuple: [path, params?]
 * @param loginType The user's login type from UserInfoContext
 * @returns The full URL with query params and portalType
 */
export function buildDirectUrlFromQueryKey(
    queryKey: readonly [string, ...unknown[]],
    loginType: BTLoginTypes,
): string {
    const [path, qsParams] = queryKey;

    const filteredParams =
        qsParams && typeof qsParams === "object"
            ? Object.entries(qsParams as Record<string, unknown>).filter(
                  ([, value]) => value !== undefined && value !== null,
              )
            : [];
    const searchString =
        filteredParams.length > 0
            ? new URLSearchParams(filteredParams as [string, string][]).toString()
            : "";
    const url = searchString ? `${path}?${searchString}` : path;
    return appendPortalTypeToUrl(url, loginType);
}

/**
 * Formats a date as yyyyMMddHHmmss for use as a rowVersion cache-busting parameter.
 * Matches the server-side format previously used in FileUtility.GetPlanThumbnailUrl.
 */
export function formatRowVersion(date: Date): string {
    const d = new Date(date);
    const pad = (n: number) => n.toString().padStart(2, "0");
    return (
        `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}` +
        `${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}`
    );
}
